import React, {
  useEffect, useMemo, useState, useCallback, useContext, useRef
} from 'react';
import { withStyles } from '@glu/theming';
import classnames from 'classnames';
import { AgGridReact } from 'ag-grid-react';
import locale from '@glu/locale';
import PropTypes from 'prop-types';
import Actions from '../RowActions/Actions';
import SubRowControl from '../SubRowControl/SubRowControl';
import { convertGlutoAgGrid } from '../Filters/conversion';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-material.css';
import styles from './Grid.styles';
import '../../themeDefaults';
import GridContext, { withGridContext } from './GridContext';
import HeaderCellRenderer from '../HeaderCell/HeaderCell';
import { defaultSubRowControlColumnDef } from './subRowControlColumn';
import suppressKeyboard from '../../utils/suppressKeyboard';
import {
  handleGridChange,
  mapRowHeight,
  withProps
} from './Grid.utils';
import {
  useAddSubRows,
  useChangeFilters,
  useResizeColumns,
  useSetColumns,
  useSetCurrentPage,
  useSetFilters,
  useSetPageSize,
  useUpdatePaging
} from './Grid.hooks';

const Grid = (props) => {
  const {
    SubRow,
    alternateRowShade,
    captureApis,
    classes,
    columnDefs,
    columnHasSubRowContent,
    columnHeaderFilters,
    convertFilters,
    data,
    defaultColDef,
    frameworkComponents: sourceFrameworkComponents,
    getShouldHideSubRowControl,
    gridHeight,
    gridId,
    gridRowHeight,
    headerSticky,
    onChange,
    onGridReady,
    records,
    rowSelection,
    serverSidePagination,
    setSelections,
    setVisibleItems,
    sizeColumnsToFit,
    subRowControlColumnDef,
    suppressPaginationPanel,
    ...rest
  } = props;
  const gridWrapperRef = useRef();
  const [capturedColumnApi, setColumnApi] = useState(null);
  const [gridApi, setGridApi] = useState(null);
  const [gridReady, setGridReady] = useState(false);

  const mergedSubRowControlColumnDef = {
    ...defaultSubRowControlColumnDef,
    ...subRowControlColumnDef
  };

  const handleGridReady = (params) => {
    setGridApi(params.api);
    setColumnApi(params.columnApi);
    setGridReady(true);
    onGridReady(params);
  };

  const frameworkComponents = {
    actions: useMemo(() => withProps({ gridWrapperRef, ...props })(Actions), [props]),
    agColumnHeader:
      /* istanbul ignore next */
      columnHeaderFilters
        ? withProps({ gridWrapperRef, ...props })(HeaderCellRenderer)
        : undefined,
    subRowControl: useMemo(() => withProps(props)(SubRowControl), [props]),
    ...sourceFrameworkComponents
  };

  /**
   * this seems to be a bug with ag-grid not respecting the data.columns in context
   * when there is no data
   */
  const hiddenColumns = useMemo(() => (records.length ? undefined : data
    .columns
    ?.filter((c) => c.hide)
    .map((c) => c.field)),
  [data.columns]);

  const memoizedColDefs = useMemo(() => {
    const addSubRowCol = (columnHasSubRowContent || SubRow) && !columnDefs.find(
      (col) => col.colId === mergedSubRowControlColumnDef.colId
    );

    const columnDefsWithSuppress = columnDefs.map((col) => ({
      ...col,
      ...(hiddenColumns && { hide: hiddenColumns.includes(col.field) }),
      suppressHeaderKeyboardEvent: suppressKeyboard,
      suppressKeyboardEvent: suppressKeyboard
    }));

    return [
      ...(addSubRowCol
        ? [{
          ...mergedSubRowControlColumnDef,
          cellClass: classes.subRowControl,
          cellRendererParams: { SubRow, getShouldHideSubRowControl }
        }]
        : []
      ),
      ...columnDefsWithSuppress.map(({ type, ...colDefRest }) => colDefRest)
    ];
  }, [SubRow, classes.subRowControl, columnDefs, columnHasSubRowContent, hiddenColumns]);

  const gridContext = useContext(GridContext);

  const getRowClass = useCallback(({ node: { rowIndex } }) => {
    /* istanbul ignore next */
    const shadeClass = alternateRowShade
      && ((rowIndex + 1) % 2 === 0)
      && classes.shadedRow;
    const heightClass = classes[mapRowHeight(gridRowHeight).className];
    return classnames(classes.row, heightClass, shadeClass);
  }, [classes, gridRowHeight, alternateRowShade]);

  useEffect(
    () => () => {
      if (gridApi) {
        gridApi.destroy();
      }
    },
    [gridApi]
  );

  useResizeColumns(props, gridApi, gridReady);

  useChangeFilters(props, gridApi);

  useUpdatePaging(props, gridApi);

  useAddSubRows(props, capturedColumnApi);

  useSetPageSize(props, gridApi);

  useSetCurrentPage(props, gridApi);

  useSetFilters(props, gridContext);

  useSetColumns(props, gridContext);

  /* istanbul ignore next */
  const styledDefaultColDef = {
    cellClass: (params) => {
      const classNames = [];
      if (params.colDef.actions) {
        classNames.push('action');
      }
      if (params.colDef.cellRenderer) {
        classNames.push('editable');
      }
      return classNames.join(' ');
    },
    ...defaultColDef,
    cellRendererParams: {
      ...(defaultColDef?.cellRendererParams || {}),
      gridRowHeight
    }
  };

  /* istanbul ignore next */
  const handleSelectionChanged = () => {
    setSelections(gridApi.getSelectedRows());
  };

  /* istanbul ignore next */
  const handleAgGridEvent = (params) => { handleGridChange(params, props, gridApi, gridReady); };

  return (
    <div
      ref={gridWrapperRef}
      className={classnames(
        classes.root,
        'ag-theme-material',
        ({
          [classes.empty]: (gridApi?.getDisplayedRowCount() === 0 || !records.length),
          [classes.headerSticky]: headerSticky
        })
      )}
      id={`${gridId}-popover-wrapper`}
    >
      <AgGridReact
        captureApis={captureApis}
        columnDefs={memoizedColDefs}
        context={{ data, onChange, records }}
        convertFilters={convertFilters}
        defaultColDef={styledDefaultColDef}
        domLayout="normal"
        frameworkComponents={frameworkComponents}
        getRowClass={getRowClass}
        gridHeight={gridHeight}
        gridId={gridId}
        headerHeight={40}
        localeText={{ noRowsToShow: locale.get('gridReact.noRowsToShow') }}
        onColumnMoved={handleAgGridEvent}
        onColumnPinned={handleAgGridEvent}
        onFirstDataRendered={handleGridReady}
        onMovingChanged={handleAgGridEvent}
        onPaginationChanged={handleAgGridEvent}
        onSelectionChanged={handleSelectionChanged}
        onSortChanged={handleAgGridEvent}
        pagination
        paginationPageSize={undefined} // required b/c ag-grid doesn't respond to initial value
        reactNext
        rowData={records}
        rowHeight={mapRowHeight(gridRowHeight).height}
        rowSelection={rowSelection}
        serverSidePagination={serverSidePagination}
        setVisibleItems={setVisibleItems}
        sizeColumnsToFit={sizeColumnsToFit}
        suppressDragLeaveHidesColumns
        suppressPaginationPanel={suppressPaginationPanel}
        suppressPropertyNamesCheck
        suppressRowClickSelection
        target="screen"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
      />
    </div>
  );
};

Grid.propTypes = {
  /**
   * Optional component to be used as a subrow for the grid. The component will receive
   * the same props as the SubRowControl cellRender.
   */
  SubRow: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

  alternateRowShade: PropTypes.bool,

  captureApis: PropTypes.func,

  classes: PropTypes.objectOf(PropTypes.string).isRequired,

  /** This gets passed down to AgGrid, but is required. */
  columnDefs: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      headerName: PropTypes.string
    })
  ).isRequired,

  /**
   * An individual column can have collapsible content. If that's the case
   * then this boolean can be true. You should use GridContext in the cellRenderer
   * of that column in this case.
   */
  columnHasSubRowContent: PropTypes.bool,

  /** Adds a filter menu to each column header with filters */
  columnHeaderFilters: PropTypes.bool,

  /**
   * Function to use to convert the data.filters prop to the format used by ag grid
   * Will be passed a single argument that is the data.filters prop of this component and
   * is expected to return the filters converted to the ag grid format suitable for passing
   * to the ag grid grid api setFilterModel() function.
   */
  convertFilters: PropTypes.func,

  data: PropTypes.shape({
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        colId: PropTypes.string
      })
    ),
    filters: PropTypes.arrayOf(
      PropTypes.shape({
        criteriaDisplay: PropTypes.string.isRequired,
        field: PropTypes.string.isRequired,
        filterData: PropTypes.shape({}).isRequired,
        id: PropTypes.string.isRequired,
        nameDisplay: PropTypes.string.isRequired,
        operator: PropTypes.oneOf(['AND', 'OR'])
      })
    ),
    grid: PropTypes.shape({}),
    page: PropTypes.number,
    pageSize: PropTypes.number,
    savedViews: PropTypes.shape({}),
    sort: PropTypes.arrayOf(
      PropTypes.shape({
        colId: PropTypes.string,
        sort: PropTypes.oneOf(['asc', 'desc'])
      })
    )
  }).isRequired,

  /** Composed of column definitions from ag grid */
  defaultColDef: PropTypes.shape({
    cellRendererParams: PropTypes.shape({})
  }),

  /** Most of these will be produced by Ag Grid */
  defaultState: PropTypes.shape({
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        colId: PropTypes.string
      })
    ),
    filters: PropTypes.arrayOf(
      PropTypes.shape({
        criteriaDisplay: PropTypes.string.isRequired,
        field: PropTypes.string.isRequired,
        filterData: PropTypes.shape({}).isRequired,
        id: PropTypes.string.isRequired,
        nameDisplay: PropTypes.string.isRequired,
        operator: PropTypes.oneOf(['AND', 'OR'])
      })
    ),
    grid: PropTypes.shape({}),
    page: PropTypes.number,
    pageSize: PropTypes.number,
    sort: PropTypes.arrayOf(
      PropTypes.shape({
        colId: PropTypes.string,
        sort: PropTypes.oneOf(['asc', 'dsc'])
      })
    )
  }),

  /** An object of React components that will be used in cell renderer */
  frameworkComponents: PropTypes.objectOf(PropTypes.func),

  getShouldHideSubRowControl: PropTypes.func,

  gridHeight: /* istanbul ignore next */ (props, propName, componentName, ...otherProps) => {
    const hasAutoHeight = props.domLayout && props.domLayout === 'autoHeight';
    if (hasAutoHeight) {
      return null;
    }
    if (!props[propName]) {
      return new Error(
        `Invalid ${propName} value: ${componentName} requires a ${propName} of number when domLayout is not set to autoHeight`
      );
    }
    return PropTypes.number(props, propName, componentName, ...otherProps);
  },

  gridId: PropTypes.string.isRequired,

  gridMinHeight: PropTypes.number,

  /** Row height */
  gridRowHeight: PropTypes.oneOf(['small', 'medium']),

  /** whether the header sticks to the top of the window */
  headerSticky: PropTypes.bool,

  /** Change event called anytime a component is called. Passes data in the shape of:
   {name: '', value: '', errors: {} }
   */
  onChange: PropTypes.func,

  /** Property to call after initial view is loaded. */
  onGridReady: PropTypes.func.isRequired,

  /** records data should match column defs, but doesn't have any particular shape */
  records: PropTypes.arrayOf(PropTypes.shape({})).isRequired,

  /** Controls single or multiple selection of rows */
  rowSelection: PropTypes.oneOf(['single', 'multiple']),

  serverSidePagination: PropTypes.bool,

  setSelections: PropTypes.func,

  setVisibleItems: PropTypes.func,

  /** Optional boolean that will use api.sizeColumnsToFit to fit the column to the container
   * when true
   */
  sizeColumnsToFit: PropTypes.bool,

  /** Pass additional props in SubRow columnDef */
  subRowControlColumnDef: PropTypes.shape({
    colId: PropTypes.string
  }),

  suppressPaginationPanel: PropTypes.bool
};
Grid.defaultProps = {
  SubRow: null,
  alternateRowShade: undefined,
  captureApis: null,
  columnHasSubRowContent: false,
  columnHeaderFilters: true,
  convertFilters: convertGlutoAgGrid,
  defaultColDef: {},
  defaultState: {},
  frameworkComponents: {},
  getShouldHideSubRowControl: undefined,
  gridHeight: undefined,
  gridMinHeight: 150,
  gridRowHeight: undefined,
  headerSticky: false,
  onChange: /* istanbul ignore next */() => { },
  rowSelection: undefined,
  serverSidePagination: false,
  setSelections: /* istanbul ignore next */() => { },
  setVisibleItems: /* istanbul ignore next */() => { },
  sizeColumnsToFit: false,
  subRowControlColumnDef: undefined,
  suppressPaginationPanel: true
};

export const BareGrid = Grid;

const StyledGrid = withStyles(styles)(Grid);

export default withGridContext(StyledGrid);
